import React, { useState, useEffect } from 'react';
import { HotelStaffMember } from '../types/admin';
import { hotelAdminService } from '../services/hotelAdminService';
import { StaffForm } from '../components/admin/StaffForm';
import type { StaffFormData } from '../components/admin/StaffForm';
import { DeleteConfirmModal } from '../components/admin/DeleteConfirmModal';

interface CreatedCredentials {
  email: string;
  password: string;
  fullName: string;
  role: string;
}

export function StaffManagementPage() {
  const [staff, setStaff] = useState<HotelStaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingStaff, setEditingStaff] = useState<HotelStaffMember | null>(null);
  const [deletingStaff, setDeletingStaff] = useState<HotelStaffMember | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState<CreatedCredentials | null>(null);

  useEffect(() => {
    let cancelled = false;
    
    const fetchStaff = async () => {
      try {
        setLoading(true);
        setError(null);
        const staffData = await hotelAdminService.getStaffMembers();
        if (!cancelled) {
          setStaff(staffData);
        }
      } catch (err) {
        console.error('Staff fetch error:', err);
        if (!cancelled) {
          setError('Erreur lors du chargement du personnel');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    
    fetchStaff();
    
    return () => { cancelled = true; };
  }, []);

  const handleRefresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const staffData = await hotelAdminService.getStaffMembers();
      setStaff(staffData);
    } catch (err) {
      setError('Erreur lors du chargement du personnel');
    } finally {
      setLoading(false);
    }
  };

  const handleAddStaff = () => {
    setEditingStaff(null);
    setShowForm(true);
  };

  const handleEditStaff = (staffMember: HotelStaffMember) => {
    setEditingStaff(staffMember);
    setShowForm(true);
  };

  const handleDeleteStaff = (staffMember: HotelStaffMember) => {
    setDeletingStaff(staffMember);
  };

  const handleSaveStaff = async (staffData: any) => {
    setActionLoading(true);
    try {
      if (editingStaff) {
        await hotelAdminService.updateStaffMember(editingStaff.id, staffData);
        const updatedStaff = staff.map(s =>
          s.id === editingStaff.id
            ? { ...s, ...staffData }
            : s
        );
        setStaff(updatedStaff);
      } else {
        const password = staffData.password || '';
        const newStaff = await hotelAdminService.createStaffMember(
          staffData as Omit<HotelStaffMember, 'id' | 'createdAt' | 'lastLogin'> & { password: string }
        );
        setStaff(prev => [...prev, newStaff]);
        setCreatedCredentials({
          email: staffData.email,
          password,
          fullName: staffData.fullName,
          role: staffData.role,
        });
      }
      setShowForm(false);
      setEditingStaff(null);
    } catch (err: any) {
      console.error('Save staff error:', err);
      setError(err.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingStaff) return;
    
    setActionLoading(true);
    try {
      await hotelAdminService.deleteStaffMember(deletingStaff.id);
      setStaff(prev => prev.filter(s => s.id !== deletingStaff.id));
      setDeletingStaff(null);
    } catch (err) {
      console.error('Delete staff error:', err);
      setError('Erreur lors de la suppression');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingStaff(null);
  };

  const handleCancelDelete = () => {
    setDeletingStaff(null);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          <span className="ml-4 text-gray-600">Chargement du personnel...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
          <button 
            onClick={handleRefresh}
            className="ml-4 text-red-800 underline hover:no-underline"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Credentials banner after creation */}
      {createdCredentials && (
        <div className="bg-green-50 border border-green-300 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-green-800">Compte cree avec succes</h4>
            <button
              onClick={() => setCreatedCredentials(null)}
              className="text-green-600 hover:text-green-800 text-sm"
            >
              Fermer
            </button>
          </div>
          <p className="text-sm text-green-700 mb-3">
            Communiquez ces identifiants au membre du personnel. Le mot de passe ne sera plus affiche.
          </p>
          <div className="bg-white rounded border border-green-200 p-3 space-y-2 font-mono text-sm">
            <div><span className="text-gray-500">Nom :</span> <strong>{createdCredentials.fullName}</strong></div>
            <div><span className="text-gray-500">Email :</span> <strong>{createdCredentials.email}</strong></div>
            <div><span className="text-gray-500">Mot de passe :</span> <strong className="select-all">{createdCredentials.password}</strong></div>
            <div><span className="text-gray-500">Role :</span> <strong>{createdCredentials.role}</strong></div>
          </div>
          <button
            onClick={() => {
              const text = `Portail Hotel - Identifiants\nNom: ${createdCredentials.fullName}\nEmail: ${createdCredentials.email}\nMot de passe: ${createdCredentials.password}\nRole: ${createdCredentials.role}`;
              navigator.clipboard.writeText(text).catch(() => {});
            }}
            className="mt-3 px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700"
          >
            Copier les identifiants
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gestion du Personnel</h1>
          <p className="text-gray-600 mt-1">
            Gérez les membres de votre équipe et leurs permissions
          </p>
        </div>
        <button
          onClick={handleAddStaff}
          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Ajouter Personnel
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{staff.length}</p>
              <p className="text-sm text-gray-600">Total Personnel</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {staff.filter(s => s.status === 'active').length}
              </p>
              <p className="text-sm text-gray-600">Personnel Actif</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {staff.filter(s => ['Administrator','ADMIN','hotel_admin','OWNER'].includes(s.role)).length}
              </p>
              <p className="text-sm text-gray-600">Administrateurs</p>
            </div>
          </div>
        </div>
      </div>

      {/* Staff List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Liste du Personnel</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {staff.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="text-gray-500 mb-4">Aucun membre du personnel</p>
              <button
                onClick={handleAddStaff}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
              >
                Ajouter le premier membre
              </button>
            </div>
          ) : (
            staff.map((member) => (
              <div key={member.id} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                      <span className="text-gray-600 font-medium">
                        {member.fullName.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <p className="font-medium text-gray-900">{member.fullName}</p>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          ['Administrator','ADMIN','hotel_admin','OWNER'].includes(member.role) ? 'bg-purple-100 text-purple-800' :
                          ['Manager','MANAGER','hotel_manager'].includes(member.role) ? 'bg-blue-100 text-blue-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {['Administrator','ADMIN','hotel_admin','OWNER'].includes(member.role) ? 'Administrateur' :
                           ['Manager','MANAGER','hotel_manager'].includes(member.role) ? 'Gestionnaire' : 'Réceptionniste'}
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          member.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {member.status === 'active' ? 'Actif' : 'Inactif'}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{member.email}</p>
                      <p className="text-xs text-gray-500">{member.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEditStaff(member)}
                      className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                      title="Modifier"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteStaff(member)}
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                      title="Supprimer"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modals */}
      {showForm && (
        <StaffForm
          staff={editingStaff || undefined}
          onSave={handleSaveStaff}
          onCancel={handleCancelForm}
          isLoading={actionLoading}
        />
      )}

      {deletingStaff && (
        <DeleteConfirmModal
          staff={deletingStaff}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          isLoading={actionLoading}
        />
      )}
    </div>
  );
}
